/**
 * Coravu Brand System — single source of truth.
 *
 * Every visual surface in the Coravu app (marketing landing, portal,
 * dashboards) should import its colors, fonts, section backgrounds, and
 * logos from this file. The unlinked brand page at `/brand` renders
 * these values as a live spec — so editing this file updates both the
 * brand page AND every surface that imports from it. No drift.
 *
 * When making UI changes, reference the brand page first. If something
 * isn't in the brand system yet, add it here before using it in code.
 */

export type ColorToken = {
  hex: string;
  role: string;
};

export const COLORS: Record<string, ColorToken> = {
  // ── Brand core ─────────────────────────────────────────────
  FOREST: { hex: "#1C2B1A", role: "Primary forest. Text on light; student topbar; admin/client sidebar." },
  FOREST_MID: { hex: "#243521", role: "Mid forest. Embedded panels on dark sections; admin cards." },
  FOREST_DEEP: { hex: "#131e12", role: "Deep forest. Marketing hero + companies section backgrounds." },
  FOREST_DEEPER: { hex: "#0A1109", role: "Deeper forest. Audit log diff panels." },
  // General/marketing-pane greens — sampled from the hero village art so the
  // marketing surfaces match the illustration. Wired ONLY into
  // PANE_THEMES.General; the portals (Student/Client/Admin/Judge) keep FOREST
  // and FOREST_DEEP, so this change does not touch them.
  FOREST_GENERAL: { hex: "#04210C", role: "General/marketing background + sidebar/topbar + loading-screen green, sampled to exactly match the hero village art's sky so the loading page and hero share one seamless green. General pane only." },
  BLACK: { hex: "#0E0E0E", role: "Page background for client + admin portals." },
  OFF_BLACK: { hex: "#161616", role: "Card surface on BLACK pages (client dashboard cards)." },

  // ── Light surfaces ─────────────────────────────────────────
  WHITE: { hex: "#FFFFFF", role: "Pure white. Input / card surfaces on light marketing + auth forms." },
  LINEN: { hex: "#F7F4EE", role: "Linen. Body / headline text on dark; student card surface." },
  SAND: { hex: "#EDE8DF", role: "Sand. Marketing 'For Students' section background." },
  SAND_GENERAL: { hex: "#E2BC84", role: "General/marketing warm sand, sampled from the hero art's bottom sand band so the section directly below the art blends seamlessly into it. General pane only." },
  OFFW: { hex: "#FCFAF5", role: "Off-white. Student editorial hero bands." },
  SAND_DARK: { hex: "#D9D2C2", role: "Sand dark. Borders & dividers on light student surfaces." },
  SAND_LINE: { hex: "#E8E2D2", role: "Sand line. Hairline rules and dashed upcoming-state borders on light student surfaces." },

  // ── Amber family ───────────────────────────────────────────
  AMBER: { hex: "#B5873A", role: "Brand amber. CTAs, eyebrows, accent dots, headline accents." },
  AMBER_WARM: { hex: "#C9983E", role: "Amber warm. Hover state for AMBER buttons." },
  AMBER_DEEP: { hex: "#9B7330", role: "Amber deep. Muted amber text on light surfaces." },
  GOLD_RM: { hex: "#C9A24A", role: "RM gold. Coravu-Managed program accent ONLY — never general use." },

  // ── Green family ───────────────────────────────────────────
  SAGE: { hex: "#8FBB8A", role: "Sage. Subtle strokes — palm-frond art, dividers, success states." },
  SAGE_DEEP: { hex: "#647F4D", role: "Sage deep. Mid-urgency badges on student deliverables." },
  LIVE_GREEN: { hex: "#7FCB7A", role: "Live indicator. Pulsing dot for active programs / phase progress." },

  // ── Red / error ────────────────────────────────────────────
  RED_ERROR: { hex: "#E69A8A", role: "Red error. Rejection states, late submissions, destructive actions." },
};

/**
 * Font role → loaded font-family name. The names match what
 * `useMarketingFonts()` loads via @expo-google-fonts. Surfaces should
 * reference a role (e.g. FONTS.bullet) instead of a raw font name.
 */
export const FONTS = {
  display: "CormorantGaramond_400Regular",
  displayLight: "CormorantGaramond_300Light",
  displayItalic: "CormorantGaramond_400Regular_Italic",
  displayMedium: "CormorantGaramond_500Medium",
  displayMediumItalic: "CormorantGaramond_500Medium_Italic",
  displaySemiBold: "CormorantGaramond_600SemiBold",
  bodyLight: "DMSans_300Light",
  body: "DMSans_400Regular",
  bodyItalic: "DMSans_400Regular_Italic",
  bodyMedium: "DMSans_500Medium",
  bodySemiBold: "DMSans_600SemiBold",
  bodyBold: "DMSans_700Bold",
} as const;

/**
 * Spacing / radius / stroke scales — the layout half of the brand system.
 * Seeded from the px values the app already used, so consuming them is a pure
 * plumbing pass (no visual change). The /brand "Spacing" tab renders these
 * live, and the shared aspect layer + portal shells pull padding,
 * border-radius, and border width from here — so editing a value moves both
 * the reference page and the real UI. Numbers off these scales (one-off
 * padding like 11/14/18, the 14px shell topbar tile, mark stroke widths) stay
 * inline by design — only on-scale values are tokenized.
 */
export const SPACING = {
  xxs: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
} as const;
export type SpacingToken = keyof typeof SPACING;

export const RADII = {
  xs: 4,
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  full: 999,
} as const;
export type RadiusToken = keyof typeof RADII;

export const STROKES = {
  hairline: 1,
  border: 1.5,
  strong: 2,
  heavy: 3,
} as const;
export type StrokeToken = keyof typeof STROKES;

export type TypeRole = {
  name: string;
  font: string;
  size: number;
  lineHeight: number;
  letterSpacing?: number;
  transform?: "uppercase" | "none";
  sample: string;
};

export const TYPE_ROLES: TypeRole[] = [
  {
    name: "Headline display",
    font: FONTS.display,
    size: 74,
    lineHeight: 84,
    sample: "Win, don't apply.",
  },
  {
    name: "Headline italic accent",
    font: FONTS.displayItalic,
    size: 74,
    lineHeight: 84,
    sample: "never told",
  },
  {
    name: "Eyebrow",
    font: FONTS.bodySemiBold,
    size: 11,
    lineHeight: 14,
    letterSpacing: 3.5,
    transform: "uppercase",
    sample: "For Students",
  },
  {
    name: "Body",
    font: FONTS.body,
    size: 16,
    lineHeight: 27,
    sample: "Compete in real business challenges to earn opportunities.",
  },
  {
    name: "Body emphasis",
    font: FONTS.bodyMedium,
    size: 16,
    lineHeight: 27,
    sample: "Let your work open the door.",
  },
  {
    name: "Bullet item",
    font: FONTS.displayMedium,
    size: 22,
    lineHeight: 30,
    sample: "Solve real company challenges",
  },
  {
    name: "Button label",
    font: FONTS.bodySemiBold,
    size: 13,
    lineHeight: 16,
    letterSpacing: 1.7,
    transform: "uppercase",
    sample: "Student Portal",
  },
];

export type SectionSpec = {
  name: string;
  bg: keyof typeof COLORS;
  text: keyof typeof COLORS;
  accent: keyof typeof COLORS;
  note: string;
};

export const SECTIONS: SectionSpec[] = [
  {
    name: "Hero",
    bg: "FOREST_DEEP",
    text: "LINEN",
    accent: "AMBER",
    note: "Top of landing. Immersive dark with palm-vein art and amber wash.",
  },
  {
    name: "For Students",
    bg: "SAND",
    text: "FOREST",
    accent: "AMBER",
    note: "Light editorial section. Bullet list, locked competition cards.",
  },
  {
    name: "For Companies",
    bg: "FOREST_DEEP",
    text: "LINEN",
    accent: "AMBER",
    note: "Dark section with embedded dashboard preview on FOREST_MID.",
  },
];

export type Logo = {
  name: string;
  /** SVG source string OR import path. Empty for now — Tyler to drop in. */
  src: string | null;
  note: string;
};

export const LOGOS: Logo[] = [
  {
    name: "Primary wordmark",
    src: null,
    note: "Full COR·A·VU lockup with sun emblem. Used in nav and footer.",
  },
  {
    name: "Sun emblem",
    src: null,
    note: "Standalone radiating-sun mark. Used as favicon and avatar.",
  },
];

export type BestPractice = {
  title: string;
  body: string;
};

export const BEST_PRACTICES: BestPractice[] = [
  {
    title: "Simple over clever",
    body: "Default to the most direct visual treatment. If a section needs more than one accent color or font weight to read, pare back before adding.",
  },
  {
    title: "Single source of truth",
    body: "Never hardcode hex or font names in components. Import from brand.ts. Drift kills brand consistency faster than bad design.",
  },
  {
    title: "Amber is the only accent",
    body: "Brand amber (#B5873A) is the one accent color. Greens (sage, live-green) are functional or atmospheric. Don't introduce new accent hues.",
  },
  {
    title: "Editorial display, clean body",
    body: "Cormorant Garamond carries all headline and emotional weight. DM Sans handles utility — body, labels, buttons. Don't mix display fonts.",
  },
];
